// Sending a command to a device — port of Flutter's devController().
//
// TWO TRANSPORTS, chosen by connectivity:
//
//   online   MQTT  `[<uno>]<message>#<userId>` on topic `<uno>/device`
//   offline  BLE   the bare command written to the device's 0xFF01 characteristic
//
// The BLE leg is what keeps a house usable with no internet: the firmware takes
// the same command strings over Bluetooth that it takes over MQTT ("1-1",
// "alloff", …), so the caller doesn't care which path was used.
//
// publishCommand stays SYNCHRONOUS for the online case, because every control
// screen calls it in a tap handler and expects no await. The BLE fallback is
// fired off in the background instead of changing that signature.

import { publishMessage } from '../mqtt/mqttClient';
import { isOffline } from '../offline/offlineMode';
import { bleAutoSupported, BLE_OFF, requestBleEnable } from '../ble/bleClient';
import { sendControlOverBle } from '../ble/bleControl';
import { isForcedBle } from '../ble/bleForced';
import { isBleCapable } from '../ble/bleCapable';

// Set by the UI so a failed offline send can say something useful.
let notify = null;
export function setControlNotifier(fn) { notify = fn; }

function overBle(uniqueNumber, message) {
  // bleAutoSupported, NOT bleSupported: this runs inside a tap handler with no
  // one having asked for Bluetooth. The web can speak BLE, but only through a
  // chooser the user has to click — see bleClient. Here that is no route at all,
  // so the honest answer is still "no internet connection".
  if (!bleAutoSupported()) {
    if (notify) notify('No internet connection.');
    return;
  }
  sendControlOverBle(uniqueNumber, message).then(async (first) => {
    let { ok, code } = first;
    // Radio off: the user has just pressed a control that needs it, so ask
    // Android to switch Bluetooth on (its own dialog) and send once more.
    if (!ok && code === BLE_OFF && await requestBleEnable()) {
      await new Promise((r) => { setTimeout(r, 800); });
      ({ ok, code } = await sendControlOverBle(uniqueNumber, message));
    }
    if (ok || !notify) return;
    // "Move closer" is useless advice when the radio is off — and it was what
    // this said, because both failures arrived as the same bare `false`.
    notify(code === BLE_OFF
      ? 'Bluetooth is switched off. Turn it on to control this device offline.'
      : 'Device not reachable over Bluetooth. Move closer and try again.');
  });
}

// Decide the transport for a control message. Returns true when the send has
// been handled off the MQTT path (routed over BLE, or reported unreachable), so
// the caller should NOT also publish.
//
//   • Forced (bench allow-list)              -> BLE, regardless of network.
//   • Offline AND the device can do BLE      -> BLE.
//   • Offline AND the device CANNOT do BLE   -> unreachable; say so.
//
// ⚠️ BLE is gated on DevicesInstalled.BLEAvailable (core/ble/bleCapable). ESP8266
// has no Bluetooth, so an offline ESP8266 is simply unreachable — publishing to
// the dead broker would vanish silently, so we tell the user instead. The whole
// current fleet is BLEAvailable = 0, so this is today's behaviour until an
// ESP32-C2/C3 chip is registered with the flag on.
function routedOverBle(uniqueNumber, message) {
  if (isForcedBle(uniqueNumber) || (isOffline() && isBleCapable(uniqueNumber))) {
    overBle(uniqueNumber, message);
    return true;
  }
  if (isOffline()) {
    if (notify) notify('No internet connection.');
    return true;
  }
  return false;
}

export function publishCommand(uniqueNumber, message, userId) {
  const payload = `[${uniqueNumber}]${message}#${userId}`;
  if (routedOverBle(uniqueNumber, message)) return payload;
  publishMessage(`${uniqueNumber}/device`, payload);
  return payload;
}

// Publish `[<uno>]<message>` — addressed, but with NO `#<userId>` suffix.
//
// ⚠️ THIS EXISTS FOR ADDWIFI / REMOVEWIFI AND NOTHING ELSE SHOULD NEED IT.
// The ESP8266 skips its `#` stripping for exactly those two verbs (a password
// may contain a '#'), so the user id ends up inside the password and the
// credential never matches what is stored — see the long note in
// core/devices/commands.js. Sending them fully bare is not the fix either: with
// no `[<uno>]` the ESP32 treats the command as a broadcast.
//
// Stays synchronous, like publishCommand, for the same reason.
export function publishAddressed(uniqueNumber, message) {
  const payload = `[${uniqueNumber}]${message}`;
  // BLE takes the bare command — the envelope is an MQTT addressing device,
  // and the link is already point-to-point.
  if (routedOverBle(uniqueNumber, message)) return payload;
  publishMessage(`${uniqueNumber}/device`, payload);
  return payload;
}

// Publish a message with NO `[uno]…#userId` envelope.
//
// Some firmware commands are sent raw — in Flutter, devController() skips the
// envelope whenever it is called with a `buttonId`, which is how `getallwifi`
// goes out. Same topic either way; only the payload differs.
export function publishRaw(uniqueNumber, message) {
  if (routedOverBle(uniqueNumber, message)) return message;
  publishMessage(`${uniqueNumber}/device`, message);
  return message;
}
